/**
 * System entities are used to identify gently-owned entities in the system,
 * especially in the case of polymorphic relationships.
 */
export enum SystemEntity {
  // CORE
  CORE_MEMORY_BANK_NOTE = "CORE_MEMORY_BANK_NOTE",
  CORE_MEMORY_BANK_NOTE_USAGE = "CORE_MEMORY_BANK_NOTE_USAGE",
  ORGANIZATION = "ORGANIZATION",
  CORE_COMPANY = "CORE_COMPANY",
  CORE_FILE = "CORE_FILE",
  CORE_DOCUMENT = "CORE_DOCUMENT",
  CORE_DOCUMENT_OCR_RESULT = "CORE_DOCUMENT_OCR_RESULT",

  // AR
  AR_CUSTOMER = "AR_CUSTOMER",
  AR_CUSTOMER_PRODUCT = "AR_CUSTOMER_PRODUCT",
  AR_STAGED_SALES_ORDER = "AR_STAGED_SALES_ORDER",
  AR_DISCOUNT_GROUP = "AR_DISCOUNT_GROUP",
  AR_DRAFT_SALES_ORDER = "AR_DRAFT_SALES_ORDER",
  AR_SALES_ORDER = "AR_SALES_ORDER",
  AR_SALES_ORDER_LINE_ITEM = "AR_SALES_ORDER_LINE_ITEM",
  /**
   * Retired with `ar_payments` (GEN-3091). Kept only as the type of the
   * never-set `payment` socket on the remittance steps, which saved workflows
   * still wire.
   */
  AR_PAYMENT = "AR_PAYMENT",
  AR_INVOICE = "AR_INVOICE",
  AR_PAYMENT_APPLICATION = "AR_PAYMENT_APPLICATION",
  AR_REMITTANCE_ADVICE = "AR_REMITTANCE_ADVICE",
  /** Bank transaction ingested as a cash receipt (`ar_bank_transactions`). */
  AR_CASH_RECEIPT = "AR_CASH_RECEIPT",
  AR_SHIPMENT = "AR_SHIPMENT",
  AR_COLLECTION_THREAD = "AR_COLLECTION_THREAD",
  AR_SAVED_REPORT = "AR_SAVED_REPORT",

  // AP
  AP_INVOICE = "AP_INVOICE",
  AP_INVOICE_LINE_ITEM = "AP_INVOICE_LINE_ITEM",
  AP_SUPPLIER = "AP_SUPPLIER",
  INTERNAL_PURCHASE_ORDER = "INTERNAL_PURCHASE_ORDER",
  INTERNAL_PURCHASE_ORDER_LINE_ITEM = "INTERNAL_PURCHASE_ORDER_LINE_ITEM",

  // FINANCE
  PAYMENT = "PAYMENT",
  FINANCIAL_TRANSACTION = "FINANCIAL_TRANSACTION",
  CHARGE = "CHARGE",

  // REBATES
  REBATE_AGREEMENT = "REBATE_AGREEMENT",

  // INBOUND EMAILS
  INBOUND_EMAIL = "INBOUND_EMAIL",
  OUTBOUND_EMAIL = "OUTBOUND_EMAIL",

  // INVENTORY MANAGEMENT
  IM_TRANSFER_ORDER = "IM_TRANSFER_ORDER",
  IM_TRANSFER_ORDER_LINE_ITEM = "IM_TRANSFER_ORDER_LINE_ITEM",
  IM_DRAFT_PURCHASE_ORDER = "IM_DRAFT_PURCHASE_ORDER",
  /**
   * One vendor price change (`im_pricelist_cases`), the anchor of the
   * companion `core_tasks` row it is paired 1:1 with. Anchoring is what makes
   * `core_tasks_open_anchor_unique` read as "at most one open task per case".
   */
  IM_PRICELIST_CASE = "IM_PRICELIST_CASE",

  // SUPPLY CHAIN MANAGEMENT
  SCM_SHIPMENT = "SCM_SHIPMENT",

  // WORKFLOWS
  WORKFLOW_DEFINITION = "WORKFLOW_DEFINITION",
  WORKFLOW_RUN = "WORKFLOW_RUN",
  STEP_RUN = "STEP_RUN",

  // TASKS
  TASK = "TASK",

  // WORKBOOKS
  WORKBOOK = "WORKBOOK",

  // AI
  AI_AGENT_RUN = "AI_AGENT_RUN",
  // TODO: This is a transitional generic entity for any AI-driven extraction
  // node (mirrors the role CORE_DOCUMENT_OCR_RESULT plays for OCR). Today it
  // only backs the AP invoice extraction result, but future extraction nodes
  // should reuse this type and differentiate via a schema-definition reference
  // (see `core_schema_definitions` / `SchemaDefinitionService`) stored on the
  // underlying row rather than by minting new SystemEntity values per domain.
  AI_EXTRACTION_RESULT = "AI_EXTRACTION_RESULT",

  // PRICING
  PRICING_SPA = "PRICING_SPA",

  // EXTERNAL REFERENCES
  EXTERNAL_SYSTEM_REFERENCE = "EXTERNAL_SYSTEM_REFERENCE",
}

export const SYSTEM_ENTITY_LABELS: Record<SystemEntity, string> = {
  [SystemEntity.CORE_MEMORY_BANK_NOTE]: "Memory Bank Note",
  [SystemEntity.CORE_MEMORY_BANK_NOTE_USAGE]: "Memory Bank Usage",
  [SystemEntity.CORE_COMPANY]: "Company",
  [SystemEntity.ORGANIZATION]: "Organization",
  [SystemEntity.CORE_FILE]: "File",
  [SystemEntity.CORE_DOCUMENT]: "Document",
  [SystemEntity.CORE_DOCUMENT_OCR_RESULT]: "OCR Result",
  [SystemEntity.AR_CUSTOMER]: "Customer",
  [SystemEntity.AR_CUSTOMER_PRODUCT]: "Product Mapping",
  [SystemEntity.AR_STAGED_SALES_ORDER]: "Sales Order Draft",
  [SystemEntity.AR_DISCOUNT_GROUP]: "Discount Group",
  [SystemEntity.AR_DRAFT_SALES_ORDER]: "Sales Order Draft",
  [SystemEntity.AR_SALES_ORDER]: "Sales Order",
  [SystemEntity.AR_SALES_ORDER_LINE_ITEM]: "Line Item",
  [SystemEntity.AR_PAYMENT]: "AR Payment",
  [SystemEntity.AR_INVOICE]: "AR Invoice",
  [SystemEntity.AR_PAYMENT_APPLICATION]: "Payment Application",
  [SystemEntity.AR_REMITTANCE_ADVICE]: "Remittance Advice",
  [SystemEntity.AR_CASH_RECEIPT]: "Cash Receipt",
  [SystemEntity.AR_SHIPMENT]: "AR Shipment",
  [SystemEntity.AR_COLLECTION_THREAD]: "Collection Thread",
  [SystemEntity.AR_SAVED_REPORT]: "AR Report",
  [SystemEntity.AP_INVOICE]: "AP Invoice",
  [SystemEntity.AP_INVOICE_LINE_ITEM]: "AP Invoice Line Item",
  [SystemEntity.AP_SUPPLIER]: "Supplier",
  [SystemEntity.INTERNAL_PURCHASE_ORDER]: "Purchase Order",
  [SystemEntity.INTERNAL_PURCHASE_ORDER_LINE_ITEM]: "PO Line Item",
  [SystemEntity.IM_TRANSFER_ORDER]: "Transfer Order",
  [SystemEntity.IM_TRANSFER_ORDER_LINE_ITEM]: "Transfer Order Line Item",
  [SystemEntity.IM_DRAFT_PURCHASE_ORDER]: "Draft Purchase Order",
  [SystemEntity.IM_PRICELIST_CASE]: "Pricelist Update Case",
  [SystemEntity.PAYMENT]: "Payment",
  [SystemEntity.FINANCIAL_TRANSACTION]: "Transaction",
  [SystemEntity.CHARGE]: "Charge",
  [SystemEntity.REBATE_AGREEMENT]: "Rebate Agreement",
  [SystemEntity.INBOUND_EMAIL]: "Inbound Email",
  [SystemEntity.OUTBOUND_EMAIL]: "Outbound Email",
  [SystemEntity.SCM_SHIPMENT]: "Shipment",
  [SystemEntity.WORKFLOW_DEFINITION]: "Workflow",
  [SystemEntity.WORKFLOW_RUN]: "Workflow Run",
  [SystemEntity.STEP_RUN]: "Step Run",
  [SystemEntity.TASK]: "Task",
  [SystemEntity.WORKBOOK]: "Workbook",
  [SystemEntity.AI_AGENT_RUN]: "Agent Run",
  [SystemEntity.AI_EXTRACTION_RESULT]: "AI Extraction Result",
  [SystemEntity.PRICING_SPA]: "SPA",
  [SystemEntity.EXTERNAL_SYSTEM_REFERENCE]: "External Reference",
};
